/**
 * Camera-receipt → structured Transaction.
 *
 * Pipeline:
 *  1) capture image
 *  2) Tesseract OCR
 *  3) regex + on-device LLM for amount/merchant/category
 *  4) confidence-scored draft for user confirm-then-commit
 */
import { capturePhoto, ocrCanvas, ocrImageFile } from '../chassis/camera.ts';
import { generate } from '../chassis/llm.ts';
import type { Category, Source, Transaction } from '../types.ts';
import { CATEGORIES } from '../types.ts';

const AMOUNT_RE = /(?:₹|rs\.?|inr)\s*([\d,]+(?:\.\d{1,2})?)/i;
const TOTAL_RE = /(?:total|amount|net|grand\s*total|to\s*pay)\s*[:\-]?\s*(?:₹|rs\.?|inr)?\s*([\d,]+(?:\.\d{1,2})?)/i;
const DATE_RE = /(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})/;

function parseAmount(text: string): number | null {
  const totalMatch = text.match(TOTAL_RE);
  if (totalMatch !== null) {
    const n = Number(totalMatch[1]?.replace(/,/g, ''));
    if (Number.isFinite(n) && n > 0) return n;
  }
  const all = [...text.matchAll(new RegExp(AMOUNT_RE.source, 'gi'))];
  const numbers = all
    .map((m) => Number(m[1]?.replace(/,/g, '') ?? 'NaN'))
    .filter((n) => Number.isFinite(n) && n > 0);
  if (numbers.length === 0) return null;
  return Math.max(...numbers);
}

function parseDate(text: string): string {
  const m = text.match(DATE_RE);
  if (m === null) return new Date().toISOString().slice(0, 10);
  const day = Number(m[1]);
  const mon = Number(m[2]);
  const yrRaw = Number(m[3]);
  if (!Number.isFinite(day) || !Number.isFinite(mon) || !Number.isFinite(yrRaw)) {
    return new Date().toISOString().slice(0, 10);
  }
  const year = yrRaw < 100 ? 2000 + yrRaw : yrRaw;
  return `${year.toString().padStart(4, '0')}-${mon.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

function guessMerchant(text: string): string {
  const firstLine = text.split('\n').find((l) => l.trim().length > 0)?.trim() ?? 'Receipt';
  return firstLine.slice(0, 40);
}

function pickCategory(text: string): Category {
  const lower = text.toLowerCase();
  const hints: Array<[Category, string[]]> = [
    ['food_delivery', ['swiggy', 'zomato', 'food', 'pizza', 'burger', 'cafe', 'restaurant', 'dosa', 'biryani']],
    ['grocery', ['zepto', 'blinkit', 'bigbasket', 'kirana', 'grocery', 'dal', 'atta', 'rice']],
    ['mobility', ['uber', 'ola', 'rapido', 'fuel', 'petrol', 'diesel', 'parking']],
    ['fashion', ['myntra', 'ajio', 'fashion', 'shirt', 'trousers', 'saree']],
    ['electronics', ['croma', 'reliance digital', 'electronics', 'iphone', 'laptop']],
    ['health', ['apollo', 'pharmacy', 'medicine', 'tata 1mg', 'pharmeasy']],
    ['entertainment', ['bookmyshow', 'pvr', 'inox', 'cinema', 'movie']],
  ];
  for (const [cat, kws] of hints) {
    if (kws.some((k) => lower.includes(k))) return cat;
  }
  return 'other';
}

export interface ReceiptDraft {
  readonly amount_inr: number;
  readonly merchant_display: string;
  readonly category: Category;
  readonly date: string;
  readonly items: string[];
  readonly confidence: number;
  readonly raw_text: string;
}

export async function receiptFromCamera(): Promise<ReceiptDraft> {
  const cap = await capturePhoto();
  const ocr = await ocrCanvas(cap.canvas, 'eng');
  return draftFromOcr(ocr.text, ocr.confidence);
}

export async function receiptFromFile(file: File): Promise<ReceiptDraft> {
  const ocr = await ocrImageFile(file, 'eng');
  return draftFromOcr(ocr.text, ocr.confidence);
}

function draftFromOcr(text: string, ocrConf: number): ReceiptDraft {
  const amount = parseAmount(text);
  return {
    amount_inr: amount ?? 0,
    merchant_display: guessMerchant(text),
    category: pickCategory(text),
    date: parseDate(text),
    items: extractItems(text),
    confidence: amount === null ? 0.2 : Math.min(0.95, ocrConf / 100),
    raw_text: text,
  };
}

function extractItems(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length >= 4 && l.length <= 60 && /[a-zA-Z]/.test(l) && !/(total|tax|gst|amount|qty|rate|date|time)/i.test(l))
    .slice(0, 10);
}

export function draftToTransaction(draft: ReceiptDraft, source: Source = 'manual'): Transaction {
  const id = `r-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  const cat = (CATEGORIES as readonly string[]).includes(draft.category) ? draft.category : 'other';
  return {
    id,
    source,
    source_ref: 'receipt-ocr',
    merchant_id: draft.merchant_display.toLowerCase().replace(/[^a-z0-9]+/g, '_').slice(0, 32),
    merchant_display: draft.merchant_display,
    amount_inr: draft.amount_inr,
    items: draft.items,
    category: cat as Category,
    date: draft.date,
    confidence: draft.confidence,
    detected_at: new Date().toISOString(),
  };
}

export async function llmRefineDraft(draft: ReceiptDraft): Promise<ReceiptDraft> {
  if (draft.raw_text.trim().length < 10) return draft;
  const prompt = `You are extracting one transaction from this receipt text. Reply with ONLY a JSON object with keys: merchant (string), amount (number, INR), category (one of: ${CATEGORIES.join(', ')}). Receipt text:\n\n${draft.raw_text.slice(0, 800)}\n\nJSON:`;
  const out = await generate(prompt, { maxTokens: 120, temperature: 0 });
  const m = out.match(/\{[\s\S]*\}/);
  if (m === null) return draft;
  try {
    const parsed = JSON.parse(m[0]) as { merchant?: string; amount?: number; category?: Category };
    return {
      ...draft,
      merchant_display: parsed.merchant ?? draft.merchant_display,
      amount_inr: typeof parsed.amount === 'number' ? parsed.amount : draft.amount_inr,
      category: typeof parsed.category === 'string' && (CATEGORIES as readonly string[]).includes(parsed.category) ? parsed.category : draft.category,
      confidence: Math.min(0.98, draft.confidence + 0.1),
    };
  } catch {
    return draft;
  }
}
